/**
 * THESIS: Booking behaves like a live course plot, refusing the promotional golf hero and generic SaaS card grid.
 * OWN-WORLD: Survey chalk, midnight instruments, routing blue, one orange fix, and precise cartographic rules.
 * STORY: Read course context, choose a day and time, then route that decision into the manifest and Stripe checkout.
 * FIRST VIEWPORT: Groundline masthead, course/date controls, abstract routing plot, chronological schedule, and sticky manifest.
 * FORM: “Course Plot,” approved composition B from grounded direction 7; concept seed fbffbaa6.
 */
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import {
  ArrowRight,
  CalendarDays,
  CarFront,
  Check,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  Clock3,
  FlagTriangleRight,
  Footprints,
  MapPin,
  Minus,
  Plus,
  UsersRound,
} from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"

import { DemoNotice } from "@/components/demo-notice"
import { CoursePlot } from "@/components/course-plot"
import { BookingHomePending } from "@/components/loading-skeleton"
import { PublicHeader } from "@/components/public-header"
import { Button } from "@/components/ui/button"
import {
  addDays,
  formatCourseDate,
  formatCurrency,
  formatMinutes,
  formatShortDate,
  isIsoDate,
} from "@/lib/date"
import type { CartPreference } from "@/lib/domain"
import { availabilityQueryOptions } from "@/lib/query-options"
import { cn } from "@/lib/utils"

type Search = {
  course?: string
  date?: string
}

export const Route = createFileRoute("/")({
  validateSearch: (search: Record<string, unknown>): Search => ({
    course: typeof search.course === "string" ? search.course : undefined,
    date:
      typeof search.date === "string" && isIsoDate(search.date)
        ? search.date
        : undefined,
  }),
  shouldReload: false,
  loader: ({ context, location }) =>
    context.queryClient.ensureQueryData(
      availabilityQueryOptions(location.search as Search)
    ),
  head: () => ({
    meta: [{ title: "Book a round · Groundline" }],
  }),
  pendingComponent: BookingHomePending,
  errorComponent: AvailabilityError,
  component: BookingHome,
})

function AvailabilityError({ error }: { error: Error }) {
  return (
    <>
      <PublicHeader viewer={null} />
      <main className="site-frame py-16">
        <div className="ruled-panel empty-state">
          <div>
            <FlagTriangleRight className="size-7" />
            <strong>The tee sheet did not load</strong>
            <p className="mt-2">{error.message}</p>
            <Button className="mt-5" onClick={() => window.location.reload()}>
              Try again
            </Button>
          </div>
        </div>
      </main>
    </>
  )
}

function BookingHome() {
  const search = Route.useSearch()
  const loaderData = Route.useLoaderData()
  const availability = useQuery({
    ...availabilityQueryOptions(search),
    placeholderData: keepPreviousData,
  })
  const queryClient = useQueryClient()
  const navigate = Route.useNavigate()
  const lastSuccessfulData = useRef(loaderData)

  useEffect(() => {
    if (availability.data && !availability.isPlaceholderData) {
      lastSuccessfulData.current = availability.data
    }
  }, [availability.data, availability.isPlaceholderData])

  const data = availability.data ?? lastSuccessfulData.current

  const activeCourseSlug =
    search.course &&
    data.courses.some((course) => course.slug === search.course)
      ? search.course
      : data.course.slug
  const activeDate =
    search.date &&
    search.date >= data.today &&
    search.date <= addDays(data.today, 9)
      ? search.date
      : data.date
  const isRefreshing = availability.isFetching
  const refreshFailed = availability.isError && !availability.isFetching
  const firstBookable = data.times.find((time) => time.isBookable)
  const [selectedId, setSelectedId] = useState(firstBookable?.id ?? "")
  const [players, setPlayers] = useState(2)
  const [cart, setCart] = useState<CartPreference>("riding")
  const [period, setPeriod] = useState<"all" | "morning" | "afternoon">("all")
  const [showAllTimes, setShowAllTimes] = useState(false)
  const selected =
    data.times.find((time) => time.id === selectedId && time.isBookable) ??
    firstBookable

  useEffect(() => {
    setSelectedId(firstBookable?.id ?? "")
  }, [data.course.id, data.date, firstBookable?.id])

  useEffect(() => {
    if (selected && players > selected.spotsLeft) {
      setPlayers(Math.max(1, selected.spotsLeft))
    }
  }, [players, selected])

  const visibleTimes = useMemo(
    () =>
      data.times.filter((time) => {
        if (period === "morning") return time.startMinutes < 720
        if (period === "afternoon") return time.startMinutes >= 720
        return true
      }),
    [data.times, period]
  )
  const dateOptions = useMemo(
    () => Array.from({ length: 5 }, (_, index) => addDays(data.today, index)),
    [data.today]
  )

  useEffect(() => {
    if (availability.isPlaceholderData) return

    for (const day of data.prefetchedDays) {
      if (day.date === data.date) continue

      const options = availabilityQueryOptions({
        course: activeCourseSlug,
        date: day.date,
      })
      queryClient.setQueryData(options.queryKey, {
        ...data,
        date: day.date,
        times: day.slots.map(
          ([
            id,
            startMinutes,
            holes,
            capacity,
            basePriceCents,
            isOpen,
            spotsLeft,
            isFuture,
          ]) => ({
            id,
            date: day.date,
            startMinutes,
            holes,
            capacity,
            basePriceCents,
            status: isOpen ? ("open" as const) : ("blocked" as const),
            spotsLeft,
            isFuture,
            isBookable: isOpen && spotsLeft > 0 && isFuture,
          })
        ),
        prefetchedDays: [],
      })
    }
  }, [
    activeCourseSlug,
    availability.isPlaceholderData,
    data.date,
    dateOptions,
    queryClient,
  ])

  const displayedTimes = showAllTimes ? visibleTimes : visibleTimes.slice(0, 12)
  const cartCount = cart === "riding" ? Math.ceil(players / 2) : 0
  const greenFees = (selected?.basePriceCents ?? 0) * players
  const total = greenFees + cartCount * data.course.cartFeeCents

  function changeSearch(next: Search) {
    navigate({
      search: {
        course: next.course ?? activeCourseSlug,
        date: next.date ?? activeDate,
      },
    })
  }

  function prefetchDate(date: string) {
    void queryClient.prefetchQuery(
      availabilityQueryOptions({
        course: activeCourseSlug,
        date,
      })
    )
  }

  return (
    <>
      <PublicHeader viewer={data.viewer} />
      <DemoNotice />
      <main>
        <section className="booking-intro">
          <div className="site-frame">
            <div className="flex flex-col justify-between gap-6 sm:gap-8 lg:flex-row lg:items-end">
              <div className="max-w-[690px]">
                <span className="eyebrow">
                  <span className="live-fix" />
                  Course live
                </span>
                <h1 className="page-title">
                  Plot your next
                  <br />
                  round.
                </h1>
                <p className="page-lede">
                  {data.checkoutMode === "local-simulation"
                    ? "Read the day, choose a time, and confirm through the local pay-at-course simulation. No account or external payment required."
                    : "Read the day, choose a time, and route your group into Stripe’s secure test checkout. No account required."}
                </p>
              </div>
              <div className="grid w-full gap-3 sm:grid-cols-2 lg:w-[430px]">
                <div className="field">
                  <label htmlFor="course">Course</label>
                  <select
                    id="course"
                    className="control"
                    value={activeCourseSlug}
                    onChange={(event) =>
                      changeSearch({ course: event.target.value })
                    }
                  >
                    {data.courses.map((course) => (
                      <option key={course.id} value={course.slug}>
                        {course.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="date">Date</label>
                  <input
                    id="date"
                    type="date"
                    className="control"
                    min={data.today}
                    max={addDays(data.today, 9)}
                    value={activeDate}
                    onChange={(event) =>
                      changeSearch({ date: event.target.value })
                    }
                  />
                </div>
              </div>
            </div>

            <div className="date-instrument mt-9 grid grid-cols-[44px_1fr_44px] items-stretch gap-2 sm:grid-cols-[44px_repeat(5,1fr)_44px]">
              <Button
                variant="outline"
                size="icon"
                aria-label="Previous day"
                disabled={activeDate <= data.today}
                onPointerEnter={() => prefetchDate(addDays(activeDate, -1))}
                onFocus={() => prefetchDate(addDays(activeDate, -1))}
                onClick={() => changeSearch({ date: addDays(activeDate, -1) })}
              >
                <ChevronLeft />
              </Button>
              {dateOptions.map((date) => (
                <button
                  key={date}
                  type="button"
                  className={cn(
                    "date-instrument__day hidden min-h-11 border px-3 py-2 text-left transition-colors sm:block",
                    date === activeDate
                      ? "date-instrument__day--active"
                      : "border-border bg-card"
                  )}
                  aria-current={date === activeDate ? "date" : undefined}
                  aria-pressed={date === activeDate}
                  onPointerEnter={() => prefetchDate(date)}
                  onFocus={() => prefetchDate(date)}
                  onClick={() => changeSearch({ date })}
                >
                  <span className="block text-[0.65rem] font-bold tracking-[0.1em] uppercase opacity-70">
                    {date === data.today ? "Today" : formatShortDate(date)}
                  </span>
                  <span className="mt-0.5 block text-sm font-semibold">
                    {formatCourseDate(date, {
                      weekday: undefined,
                      month: "long",
                    })}
                  </span>
                </button>
              ))}
              <div className="date-instrument__mobile flex min-h-11 items-center justify-center border px-3 text-center text-sm font-semibold sm:hidden">
                {formatShortDate(activeDate)}
              </div>
              <Button
                variant="outline"
                size="icon"
                aria-label="Next day"
                disabled={activeDate >= addDays(data.today, 9)}
                onPointerEnter={() => prefetchDate(addDays(activeDate, 1))}
                onFocus={() => prefetchDate(addDays(activeDate, 1))}
                onClick={() => changeSearch({ date: addDays(activeDate, 1) })}
              >
                <ChevronRight />
              </Button>
            </div>
          </div>
        </section>

        <span className="sr-only" role="status" aria-live="polite">
          {isRefreshing
            ? `Loading tee times for ${formatCourseDate(activeDate)}`
            : refreshFailed
              ? `Tee times for ${formatCourseDate(activeDate)} did not load`
              : ""}
        </span>
        <section
          className="course-workspace"
          aria-busy={isRefreshing || undefined}
        >
          {refreshFailed ? (
            <div className="course-workspace__refresh course-workspace__refresh--error">
              <div
                className="course-workspace__refresh-status course-workspace__refresh-status--error"
                role="alert"
              >
                <FlagTriangleRight className="size-4" />
                <span>
                  <strong>Selected day unavailable</strong>
                  <small>Your current booking choices are unchanged.</small>
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void availability.refetch()}
                >
                  Retry
                </Button>
              </div>
            </div>
          ) : isRefreshing ? (
            <div className="course-workspace__refresh" aria-hidden="true">
              <span className="course-workspace__refresh-status">
                <span className="course-workspace__refresh-fix" />
                Updating course data
              </span>
            </div>
          ) : null}
          <div
            className={cn(
              "course-workspace__data groundline-booking-grid site-frame grid gap-6 py-8",
              (isRefreshing || refreshFailed) &&
                "course-workspace__data--refreshing"
            )}
            inert={isRefreshing || refreshFailed ? true : undefined}
          >
            <CoursePlot
              course={data.course.name}
              location={data.course.location}
              date={data.date}
            />
            <div className="ruled-panel course-schedule min-w-0 overflow-hidden">
              <div className="ruled-panel__head flex-wrap">
                <div>
                  <span className="eyebrow">
                    <CalendarDays className="size-3.5" />
                    {formatCourseDate(data.date)}
                  </span>
                  <h2 className="mt-1.5 text-sm font-semibold">
                    {data.course.name}
                  </h2>
                  <p className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                    <MapPin className="size-3" />
                    {data.course.location}
                  </p>
                </div>
                <div
                  className="course-filter flex border border-border bg-muted p-1"
                  role="group"
                  aria-label="Filter tee times"
                >
                  {(["all", "morning", "afternoon"] as const).map((value) => (
                    <button
                      key={value}
                      type="button"
                      className="min-h-11 px-3 text-xs font-semibold capitalize"
                      data-selected={period === value ? "" : undefined}
                      aria-pressed={period === value}
                      onClick={() => setPeriod(value)}
                    >
                      {value}
                    </button>
                  ))}
                </div>
              </div>

              {visibleTimes.length === 0 ? (
                <div className="empty-state">
                  <div>
                    <Clock3 className="size-6" />
                    <strong>No times in this window</strong>
                    <p className="mt-2">Try the other half of the day.</p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="relative pl-5 sm:pl-7">
                    <span
                      className="course-time-rail absolute top-0 bottom-0 left-[21px] w-px sm:left-[29px]"
                      aria-hidden="true"
                    />
                    {displayedTimes.map((time) => {
                      const active = selected?.id === time.id
                      return (
                        <button
                          key={time.id}
                          type="button"
                          disabled={!time.isBookable}
                          aria-pressed={active}
                          onClick={() => setSelectedId(time.id)}
                          className={cn(
                            "rule-row course-time-row relative grid w-full grid-cols-[82px_1fr_auto] items-center gap-3 py-3.5 pr-4 pl-7 text-left transition-colors sm:grid-cols-[110px_1fr_135px_auto] sm:gap-5 sm:pr-5 sm:pl-9",
                            active && "course-time-row--active",
                            time.isBookable
                              ? "course-time-row--bookable"
                              : "cursor-not-allowed opacity-48"
                          )}
                        >
                          <span
                            className={cn(
                              "course-time-node absolute left-[-4px] z-10 size-2.5 sm:left-[-4px]",
                              active && "course-time-node--active size-3"
                            )}
                            aria-hidden="true"
                          />
                          <span className="font-display text-[1.45rem] leading-none font-semibold tracking-[-0.01em]">
                            {formatMinutes(time.startMinutes)}
                          </span>
                          <span className="min-w-0">
                            <span className="block text-xs font-semibold">
                              {time.holes} holes ·{" "}
                              {formatCurrency(
                                time.basePriceCents,
                                data.course.currency
                              )}{" "}
                              / player
                            </span>
                            <span className="mt-1 hidden text-[0.7rem] text-muted-foreground sm:block">
                              {data.checkoutMode === "local-simulation"
                                ? "Local pay-at-course simulation"
                                : "Stripe test checkout · 31-minute hold"}
                            </span>
                          </span>
                          <span
                            className={cn(
                              "hidden text-xs font-semibold sm:block",
                              time.spotsLeft <= 1
                                ? "text-accent"
                                : "text-muted-foreground"
                            )}
                          >
                            {time.status === "blocked"
                              ? "Blocked"
                              : !time.isFuture
                                ? "Passed"
                                : time.spotsLeft === 0
                                  ? "Full"
                                  : `${time.spotsLeft} ${
                                      time.spotsLeft === 1 ? "spot" : "spots"
                                    } left`}
                          </span>
                          <span
                            className={cn(
                              "course-time-action grid size-9 place-items-center border",
                              active && "course-time-action--active"
                            )}
                            aria-hidden="true"
                          >
                            {active ? (
                              <Check className="size-4" />
                            ) : (
                              <ArrowRight className="size-4" />
                            )}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                  {visibleTimes.length > 12 ? (
                    <button
                      type="button"
                      className="course-time-more"
                      aria-expanded={showAllTimes}
                      onClick={() => setShowAllTimes((value) => !value)}
                    >
                      {showAllTimes
                        ? "Show fewer tee times"
                        : `Show ${visibleTimes.length - 12} more tee times`}
                    </button>
                  ) : null}
                </>
              )}
            </div>

            <aside className="ruled-panel booking-manifest overflow-hidden lg:sticky lg:top-[94px]">
              <div className="booking-manifest__head border-b border-border px-5 py-4">
                <span className="plot-label">Booking manifest</span>
                <div className="mt-2 flex items-end justify-between gap-4">
                  <div>
                    <strong className="block font-display text-[2rem] leading-none font-semibold uppercase">
                      {selected
                        ? formatMinutes(selected.startMinutes)
                        : "Choose a time"}
                    </strong>
                    <span className="mt-1.5 block text-xs">
                      {formatCourseDate(data.date, {
                        weekday: "short",
                        month: "short",
                      })}
                    </span>
                  </div>
                  <FlagTriangleRight
                    className="mb-1 size-6 text-accent"
                    aria-hidden="true"
                  />
                </div>
              </div>

              <div className="grid gap-5 p-5">
                <div className="field">
                  <span>Players</span>
                  <div className="flex min-h-12 items-center justify-between rounded-[9px] border border-border bg-muted px-2">
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Remove a player"
                      disabled={players <= 1}
                      onClick={() => setPlayers((value) => value - 1)}
                    >
                      <Minus />
                    </Button>
                    <span className="flex items-center gap-2 font-semibold">
                      <UsersRound className="size-4 text-muted-foreground" />
                      {players} {players === 1 ? "player" : "players"}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Add a player"
                      disabled={
                        !selected || players >= Math.min(4, selected.spotsLeft)
                      }
                      onClick={() => setPlayers((value) => value + 1)}
                    >
                      <Plus />
                    </Button>
                  </div>
                </div>

                <fieldset className="field">
                  <legend>Round transport</legend>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      {
                        value: "walking" as const,
                        label: "Walking",
                        icon: Footprints,
                      },
                      {
                        value: "riding" as const,
                        label: "Riding",
                        icon: CarFront,
                      },
                    ].map(({ value, label, icon: Icon }) => (
                      <label
                        key={value}
                        className={cn(
                          "choice-card flex min-h-11 cursor-pointer items-center gap-2 border px-3 text-xs font-semibold",
                          cart === value
                            ? "choice-card--selected"
                            : "bg-paper border-border text-muted-foreground"
                        )}
                      >
                        <input
                          type="radio"
                          className="sr-only"
                          name="cart"
                          value={value}
                          checked={cart === value}
                          onChange={() => setCart(value)}
                        />
                        <Icon className="size-4" />
                        {label}
                      </label>
                    ))}
                  </div>
                </fieldset>

                <div className="border-y border-dashed border-border py-4 text-xs">
                  <div className="flex justify-between gap-3 text-muted-foreground">
                    <span>Green fees × {players}</span>
                    <span>
                      {formatCurrency(greenFees, data.course.currency)}
                    </span>
                  </div>
                  {cart === "riding" ? (
                    <div className="mt-2 flex justify-between gap-3 text-muted-foreground">
                      <span>Shared cart × {cartCount}</span>
                      <span>
                        {formatCurrency(
                          cartCount * data.course.cartFeeCents,
                          data.course.currency
                        )}
                      </span>
                    </div>
                  ) : null}
                  <div className="mt-4 flex items-end justify-between gap-3">
                    <span className="flex items-center gap-1.5 font-semibold">
                      <CircleDollarSign className="size-4 text-primary" />
                      Total
                    </span>
                    <strong className="font-display text-[1.8rem] leading-none font-semibold">
                      {formatCurrency(total, data.course.currency)}
                    </strong>
                  </div>
                </div>

                {selected ? (
                  <Button
                    render={
                      <Link
                        to="/book/$teeTimeId"
                        params={{ teeTimeId: selected.id }}
                        search={{ players, cart }}
                      />
                    }
                    variant="accent"
                    size="lg"
                    className="w-full"
                  >
                    Continue to players
                    <ArrowRight data-icon="inline-end" />
                  </Button>
                ) : (
                  <Button disabled size="lg" className="w-full">
                    No bookable time selected
                  </Button>
                )}
                <p className="-mt-2 text-center text-[0.69rem] leading-5 text-muted-foreground">
                  {data.checkoutMode === "local-simulation"
                    ? "Local simulation — no payment request leaves this browser."
                    : "Stripe test mode — use card 4242 4242 4242 4242."}
                </p>
              </div>
            </aside>
            {selected ? (
              <div
                className="booking-mobile-tray"
                role="region"
                aria-label="Selected tee time"
              >
                <span>
                  <small>Selected tee time</small>
                  <strong>
                    {formatMinutes(selected.startMinutes)} ·{" "}
                    {formatCurrency(total, data.course.currency)}
                  </strong>
                </span>
                <Button
                  render={
                    <Link
                      to="/book/$teeTimeId"
                      params={{ teeTimeId: selected.id }}
                      search={{ players, cart }}
                      aria-label={`Continue with the ${formatMinutes(
                        selected.startMinutes
                      )} tee time`}
                    />
                  }
                  variant="accent"
                  size="sm"
                >
                  Continue
                  <ArrowRight data-icon="inline-end" />
                </Button>
              </div>
            ) : null}
          </div>
        </section>
      </main>
    </>
  )
}
